import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { COLLAPSED_WIDTH, EXPANDED_WIDTH, SIDE_BAR_FOOTER_TAB } from '../../helpers/constants';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';

const SideBarNavItem = ({ item, collapsed, activeItemId, onItemSelect }) => {
  const active = activeItemId != null && item.id === activeItemId;
  const Icon = item.icon;

  const content = (
    <>
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: active ? 'rgba(250, 204, 20, 0.22)' : 'transparent',
          color: active ? '#0f172a' : '#64748b',
          transition: 'background 0.2s ease, color 0.2s ease'
        }}
      >
        <Icon size={20} strokeWidth={active ? 2.25 : 2} aria-hidden />
      </span>
      {!collapsed && (
        <span
          className="flex-grow-1 text-left font-weight-semibold"
          style={{
            fontSize: 14,
            letterSpacing: '-0.01em',
            color: active ? '#0f172a' : '#475569',
            transition: 'color 0.2s ease'
          }}
        >
          {item.name}
        </span>
      )}
    </>
  );

  const baseClass =
    'd-flex align-items-center text-decoration-none border-0 bg-transparent w-100 ' + 'side-bar-nav-item rounded-lg';
  const paddingStyle = collapsed
    ? { padding: '8px 0', justifyContent: 'center' }
    : { padding: '8px 12px', gap: 12, justifyContent: 'flex-start' };

  const inactiveHover = e => {
    if (!active) {
      e.currentTarget.style.background = 'rgba(248, 250, 252, 0.95)';
    }
  };
  const leaveHover = e => {
    if (!active) {
      e.currentTarget.style.background = 'transparent';
    }
  };

  const activeStyle = {
    ...paddingStyle,
    background: active ? 'rgba(255, 251, 235, 0.95)' : 'transparent',
    boxShadow: active ? 'inset 3px 0 0 #FACC14' : 'none',
    transition: 'background 0.2s ease, box-shadow 0.2s ease'
  };

  const btn = (
    <button
      type="button"
      className={baseClass}
      style={activeStyle}
      onMouseEnter={inactiveHover}
      onMouseLeave={leaveHover}
      onClick={() => onItemSelect?.(item.id)}
      aria-current={active ? 'page' : undefined}
    >
      {content}
    </button>
  );

  return collapsed ? (
    <OverlayTrigger
      placement="right"
      delay={{ show: 200, hide: 0 }}
      overlay={<Tooltip id={`tip-${item.id}`}>{item.name}</Tooltip>}
    >
      <span className="d-block">{btn}</span>
    </OverlayTrigger>
  ) : (
    btn
  );
};

const SideBarFooterButton = ({ item, collapsed }) => {
  const Icon = item.icon;
  const SecondaryIcon = item.secondaryIcon;
  const paddingStyle = collapsed
    ? { padding: '8px 0', justifyContent: 'center' }
    : { padding: '8px 12px', gap: 12, justifyContent: 'flex-start' };

  const footerTheme = SIDE_BAR_FOOTER_TAB[item.id] || SIDE_BAR_FOOTER_TAB['manage-counties'];

  const content = (
    <>
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: footerTheme.iconTile,
          color: footerTheme.label,
          transition: 'background 0.2s ease, color 0.2s ease'
        }}
      >
        <Icon size={20} strokeWidth={2.25} aria-hidden />
      </span>
      {!collapsed && (
        <>
          <span
            className="flex-grow-1 text-left font-weight-semibold"
            style={{
              fontSize: 14,
              letterSpacing: '-0.01em',
              color: footerTheme.label,
              transition: 'color 0.2s ease'
            }}
          >
            {item.name}
          </span>
          <span
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 20,
              height: 20,
              color: footerTheme.label
            }}
          >
            {React.isValidElement(SecondaryIcon) ? (
              SecondaryIcon
            ) : SecondaryIcon ? (
              <SecondaryIcon size={14} strokeWidth={2.25} aria-hidden />
            ) : null}
          </span>
        </>
      )}
    </>
  );

  const button = (
    <button
      type="button"
      className="d-flex align-items-center border-0 w-100 side-bar-nav-item rounded-lg bg-transparent"
      style={{
        cursor: 'pointer',
        ...paddingStyle,
        background: footerTheme.rowBg,
        boxShadow: footerTheme.inset,
        transition: 'background 0.2s ease, box-shadow 0.2s ease'
      }}
      onClick={item.onClick}
      onMouseEnter={e => {
        e.currentTarget.style.background = footerTheme.rowHover;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = footerTheme.rowBg;
      }}
    >
      {content}
    </button>
  );

  return collapsed ? (
    <OverlayTrigger
      placement="right"
      delay={{ show: 200, hide: 0 }}
      overlay={<Tooltip id={`footer-tip-${item.id}`}>{item.name}</Tooltip>}
    >
      <span className="d-block">{button}</span>
    </OverlayTrigger>
  ) : (
    button
  );
};

const SideBar = ({
  items,
  title = 'Back Office',
  storageKey = 'back-office-sidebar-collapsed',
  onCollapsedChange,
  footerItems = [],
  activeItemId,
  onItemSelect,
  isMobile = false,
  mobileOpen = false,
  onRequestClose,
  children
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(storageKey, collapsed ? '1' : '0');

    onCollapsedChange?.(collapsed);
  }, [collapsed, storageKey, onCollapsedChange]);

  const toggle = useCallback(() => setCollapsed(c => !c), []);

  const width = isMobile ? EXPANDED_WIDTH : collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const mainMargin = useMemo(() => {
    if (isMobile) return 0;
    return collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
  }, [isMobile, collapsed]);

  const SideBarContent = () => (
    <>
      {isMobile && mobileOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={onRequestClose}
          className="border-0 p-0"
          style={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            zIndex: 98
          }}
        />
      )}
      <aside
        className="back-office-side-bar d-flex flex-column flex-shrink-0 position-fixed"
        style={{
          top: 64,
          left: 0,
          bottom: 0,
          width,
          zIndex: 99,
          transform: isMobile ? (mobileOpen ? 'translateX(0)' : `translateX(-${EXPANDED_WIDTH + 8}px)`) : 'none',
          transition: isMobile
            ? 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        aria-label="Back office navigation"
      >
        <div
          className="d-flex align-items-center flex-shrink-0 border-bottom"
          style={{
            minHeight: 56,
            padding: collapsed ? '12px 8px' : '12px 16px',
            borderColor: '#e2e8f0',
            justifyContent: collapsed ? 'center' : 'space-between',
            gap: 8
          }}
        >
          {!collapsed && (
            <div className="d-flex align-items-center min-w-0" style={{ gap: 10 }}>
              <span
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #FACC14 0%, #fbbf24 100%)',
                  color: '#0f172a',
                  boxShadow: '0 2px 8px rgba(250, 204, 20, 0.35)'
                }}
              >
                <LayoutGrid size={20} strokeWidth={2.25} aria-hidden />
              </span>
              <span
                className="font-weight-bold text-truncate"
                style={{ fontSize: 15, color: '#0f172a', letterSpacing: '-0.02em' }}
              >
                {title}
              </span>
            </div>
          )}
          {!isMobile && collapsed && (
            <OverlayTrigger placement="right" overlay={<Tooltip id="expand-sidebar">{title}</Tooltip>}>
              <span className="d-inline-flex">
                <button
                  type="button"
                  className="d-flex align-items-center justify-content-center border-0 rounded-lg bg-transparent"
                  style={{
                    width: 40,
                    height: 40,
                    color: '#64748b',
                    transition: 'background 0.2s ease, color 0.2s ease'
                  }}
                  onClick={toggle}
                  aria-expanded={!collapsed}
                  aria-label="Expand sidebar"
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#0f172a';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <ChevronRight size={22} />
                </button>
              </span>
            </OverlayTrigger>
          )}
          {!isMobile && !collapsed && (
            <button
              type="button"
              className="d-flex align-items-center justify-content-center border-0 rounded-lg flex-shrink-0"
              style={{
                width: 36,
                height: 36,
                color: '#64748b',
                background: '#f1f5f9',
                transition: 'background 0.2s ease, color 0.2s ease'
              }}
              onClick={toggle}
              aria-expanded={!collapsed}
              aria-label="Collapse sidebar"
              onMouseEnter={e => {
                e.currentTarget.style.background = '#e2e8f0';
                e.currentTarget.style.color = '#0f172a';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              className="d-flex align-items-center justify-content-center border-0 rounded-lg flex-shrink-0"
              style={{
                width: 36,
                height: 36,
                color: '#64748b',
                background: '#f1f5f9',
                transition: 'background 0.2s ease, color 0.2s ease'
              }}
              onClick={onRequestClose}
              aria-label="Close sidebar"
            >
              <ChevronLeft size={20} />
            </button>
          )}
        </div>

        <nav className="flex-grow-1 overflow-auto py-3 px-2" style={{ minHeight: 0 }}>
          <ul className="list-unstyled mb-0 p-0" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map(item => (
              <li
                key={item.id}
                style={{
                  backgroundColor: activeItemId === item.id ? '#facc1410' : 'transparent',
                  borderRadius: '10px'
                }}
              >
                <SideBarNavItem
                  item={item}
                  collapsed={collapsed}
                  activeItemId={activeItemId}
                  onItemSelect={onItemSelect}
                />
              </li>
            ))}
          </ul>
        </nav>

        {footerItems.length > 0 && (
          <div
            className="flex-shrink-0 border-top mt-auto"
            style={{
              borderColor: '#e2e8f0',
              padding: collapsed ? '12px 8px' : '12px 10px',
              background: 'linear-gradient(180deg, rgba(248,250,252,0.5) 0%, #ffffff 100%)'
            }}
          >
            <ul className="list-unstyled mb-0 p-0" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {footerItems.map(item => (
                <li key={item.id}>
                  <SideBarFooterButton item={item} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </>
  );

  if (children) {
    return (
      <div className="d-flex w-100 position-relative" style={{ minHeight: 'calc(100vh - 64px)', overflowX: 'hidden' }}>
        <SideBarContent />
        <main
          className="d-flex flex-column"
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            marginLeft: mainMargin,
            transition: 'margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            minHeight: 'calc(100vh - 64px)'
          }}
        >
          {children}
        </main>
      </div>
    );
  }

  return <SideBarContent />;
};

export default SideBar;
